import { useRouterState } from '@tanstack/react-router';
import { useEffect } from 'react';

import { apiUrl, tenantStore, tokenStore } from './api';
import { currentLocale, type Locale } from './i18n';

/* ---------------------------------------------------------------------------
   אנליטיקת שימוש first-party — בדיוק מה שמדיניות הפרטיות מבטיחה, ולא יותר:

   • צפייה בדף: *תבנית* המסלול (`/customers/$customerId`) — לעולם לא המזהה
     האמיתי, לא query string ולא טקסט חיפוש.
   • לחיצה: רק על אלמנט שמסומן במפורש `data-track="nav.quotes"` — קוד קבוע.
     לעולם לא הטקסט שעל המסך ולא aria-label (שם של לקוח יכול להופיע שם).
   • לכל אירוע: שפת הממשק, גודל מסך גס (sm/md/lg), ומזהה אקראי לטאב
     (sessionStorage — נעלם עם סגירת הטאב). הטננט והמשתמש נקבעים בשרת מהטוקן.
   • בלי עוגיות ובלי צד שלישי. רק במסכים המחוברים (AppShell) — לא בדפים
     ציבוריים. כבוי בבדיקות.

   השליחה ב-fetch עם keepalive ולא sendBeacon: צריך את כותרת ה-Authorization.
   --------------------------------------------------------------------------- */

export type ScreenBucket = 'sm' | 'md' | 'lg';

export type AnalyticsEvent =
  | {
      type: 'page_view';
      route: string;
      lang: Locale;
      screen: ScreenBucket;
      sessionId: string;
      ts: number;
    }
  | {
      type: 'click';
      code: string;
      route: string;
      lang: Locale;
      screen: ScreenBucket;
      sessionId: string;
      ts: number;
    };

/** זהה ל-ROUTE_TEMPLATE_RE בשרת (src/modules/analytics/analytics-events.ts). */
const ROUTE_TEMPLATE_RE = /^(\/|(\/([a-z]+(-[a-z]+)*|\$[A-Za-z]+)){1,6})$/;
const TRACK_CODE_RE = /^[a-z0-9_.-]{1,64}$/;

export const FLUSH_INTERVAL_MS = 10_000;
export const MAX_BATCH = 50;
const MAX_BUFFER = 200;

/**
 * `fullPath` של המסלול שנמצא (`/customers/$customerId`) → תבנית בטוחה, או null.
 * כל מה שלא נראה כמו תבנית (ספרות, query, טקסט) נזרק — לא "מתוקן".
 */
export function toRouteTemplate(fullPath: string | null | undefined): string | null {
  if (!fullPath) return null;
  let p = fullPath;
  if (p.length > 1) p = p.replace(/\/+$/, '');
  if (p === '') p = '/';
  return p.length <= 128 && ROUTE_TEMPLATE_RE.test(p) ? p : null;
}

export function isTrackCode(code: string | null | undefined): code is string {
  return typeof code === 'string' && TRACK_CODE_RE.test(code);
}

export function screenBucket(width: number): ScreenBucket {
  if (width < 640) return 'sm';
  if (width < 1024) return 'md';
  return 'lg';
}

export interface TrackerDeps {
  send: (events: AnalyticsEvent[]) => void;
  lang: () => Locale;
  width: () => number;
  sessionId: () => string;
  now: () => number;
}

export interface Tracker {
  /** צפייה: התבנית של המסלול, והנתיב האמיתי רק כדי לא לספור פעמיים (לא נשלח). */
  pageView: (fullPath: string | null | undefined, pathname: string) => void;
  /** המאזין המואצל ללחיצות. */
  onClick: (e: Event) => void;
  flush: () => void;
  /** מתקין מאזינים וטיימר; מחזיר הסרה. */
  install: (doc: Document, win: Window) => () => void;
  /** לבדיקות. */
  pending: () => readonly AnalyticsEvent[];
}

export function createTracker(deps: TrackerDeps): Tracker {
  let buffer: AnalyticsEvent[] = [];
  let currentRoute: string | null = null;
  let lastPathname: string | null = null;

  const base = () => ({
    lang: deps.lang(),
    screen: screenBucket(deps.width()),
    sessionId: deps.sessionId(),
    ts: deps.now(),
  });

  const push = (e: AnalyticsEvent) => {
    buffer.push(e);
    if (buffer.length > MAX_BUFFER) buffer = buffer.slice(-MAX_BUFFER);
  };

  const flush = () => {
    if (buffer.length === 0) return;
    const out = buffer;
    buffer = [];
    for (let i = 0; i < out.length; i += MAX_BATCH) deps.send(out.slice(i, i + MAX_BATCH));
  };

  const pageView: Tracker['pageView'] = (fullPath, pathname) => {
    const route = toRouteTemplate(fullPath);
    currentRoute = route;
    // StrictMode מריץ effects פעמיים; ניווט לאותו נתיב אינו צפייה חדשה.
    if (!route || pathname === lastPathname) return;
    lastPathname = pathname;
    push({ type: 'page_view', route, ...base() });
  };

  const onClick = (e: Event) => {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const el = target.closest('[data-track]');
    // רק הקוד. לא textContent, לא aria-label, לא href.
    const code = el?.getAttribute('data-track');
    if (!isTrackCode(code) || !currentRoute) return;
    push({ type: 'click', code, route: currentRoute, ...base() });
  };

  const install = (doc: Document, win: Window) => {
    const onHidden = () => {
      if (doc.visibilityState === 'hidden') flush();
    };
    doc.addEventListener('click', onClick, { capture: true });
    doc.addEventListener('visibilitychange', onHidden);
    win.addEventListener('pagehide', flush);
    const timer = win.setInterval(flush, FLUSH_INTERVAL_MS);
    return () => {
      flush();
      win.clearInterval(timer);
      doc.removeEventListener('click', onClick, { capture: true });
      doc.removeEventListener('visibilitychange', onHidden);
      win.removeEventListener('pagehide', flush);
    };
  };

  return { pageView, onClick, flush, install, pending: () => buffer };
}

/* ---------------------------------------------------------------------------
   החיבור לאפליקציה.
   --------------------------------------------------------------------------- */

const SESSION_KEY = 'craftmind.analytics.sid';

function tabSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) return existing;
    const id = crypto.randomUUID();
    sessionStorage.setItem(SESSION_KEY, id);
    return id;
  } catch {
    return crypto.randomUUID();
  }
}

/** שליחה שלא מחכים לה. כשל — מוותרים: אנליטיקה לא שווה ניסיון חוזר, וגם לא ניתוק (401). */
function sendEvents(events: AnalyticsEvent[]): void {
  const token = tokenStore.get();
  if (!token) return;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };
  const tenant = tenantStore.get() ?? import.meta.env.VITE_TENANT;
  if (tenant) headers['X-Tenant'] = tenant;
  try {
    void fetch(apiUrl('/analytics/events'), {
      method: 'POST',
      headers,
      body: JSON.stringify({ events }),
      keepalive: true,
      credentials: import.meta.env.VITE_API_URL ? 'include' : 'same-origin',
    }).catch(() => undefined);
  } catch {
    /* fetch לא זמין — מוותרים */
  }
}

let shared: Tracker | null = null;
function appTracker(): Tracker {
  shared ??= createTracker({
    send: sendEvents,
    lang: currentLocale,
    width: () => window.innerWidth,
    sessionId: tabSessionId,
    now: () => Date.now(),
  });
  return shared;
}

/** כבוי בבדיקות (vitest) — בדיקות ה-tracker יוצרות אחד משלהן. */
const ENABLED = import.meta.env.MODE !== 'test';

/** התבנית של המסלול העמוק ביותר שנמצא — לא הנתיב. */
function leafTemplate(
  matches: ReadonlyArray<{ routeId: string; fullPath: string }>,
): string | null {
  const leaf = matches[matches.length - 1];
  return leaf && leaf.routeId !== '__root__' ? leaf.fullPath : null;
}

/**
 * נקרא מ-AppShell — כלומר רק במסכים המחוברים. `active` = יש משתמש מחובר.
 */
export function useAnalytics(active: boolean): void {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const template = useRouterState({ select: (s) => leafTemplate(s.matches) });
  const on = ENABLED && active;

  useEffect(() => {
    if (!on) return;
    return appTracker().install(document, window);
  }, [on]);

  useEffect(() => {
    if (on) appTracker().pageView(template, pathname);
  }, [on, template, pathname]);
}
